import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField } from "@/components/ui-kit/form";
import { Textarea } from "@/components/ui-kit/textarea";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui-kit/button";
import { ArrowUp, Ban } from "lucide-react";
import { chatbotChatInputSchema, chatbotChatInputDefaultValue, ChatbotChatInput } from "./utils";
import { ChatbotWidgetDesignSettings } from "./index.type";

type PreviewChatInputProps = {
  config: ChatbotWidgetDesignSettings;
  onSubmit: (value: string) => void;
  disable: boolean;
};

export const PreviewChatInput = ({ config, onSubmit, disable }: PreviewChatInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const form = useForm<ChatbotChatInput>({
    defaultValues: chatbotChatInputDefaultValue,
    resolver: zodResolver(chatbotChatInputSchema),
  });
  const value = form.watch("query");
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value]);
  const submitHandler = async (value: ChatbotChatInput) => {
    if (disable) return;
    onSubmit(value.query.trim());
    form.reset();
  };
  return (
    <Form {...form}>
      <form
        className="w-full border-t bg-background pb-5 pl-6 pr-6"
        style={{ borderTopColor: config.borderColor }}
        onSubmit={form.handleSubmit(submitHandler)}
      >
        <div
          className="mt-2 flex items-center gap-1 rounded-sm border p-2.5"
          style={{ borderColor: config.borderColor }}
        >
          <FormField
            control={form.control}
            name="query"
            render={({ field }) => (
              <Textarea
                id={'chatbot-widget-input'}
                rows={1}
                placeholder="Type a message..."
                className="max-h-[120px] min-h-fit flex-1 resize-none border-0 bg-transparent p-0 text-sm text-medium-emphasis focus-visible:ring-0 focus-visible:ring-offset-0"
                {...field}
                ref={(e) => {
                  field.ref(e);
                  textareaRef.current = e;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    form.handleSubmit(submitHandler)();
                  }
                }}
              />
            )}
          />

          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 rounded-sm p-1.5"
            style={{
              backgroundColor: config.sendButtonBackgroundColor,
              color: config.sendButtonFontColor,
            }}
            disabled={disable}
            type="submit"
          >
            {disable ? (
              <Ban className="aspect-square w-4 text-error" />
            ) : (
              <ArrowUp className="aspect-square w-4" />
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
